import { state, getActiveSettings, getTeams, countedRoundsFor } from '../state.js';
import { formatPlayerName, escapeHtml } from '../utils/formatting.js';
import { getCachedListConfig, ensureListConfig } from '../services/player-db.js';
import { isYouthTournament } from '../utils/player-ratings.js';
import { refreshActiveStandingsView } from './standings-views.js';
import { applyColumnVisibility } from './column-views.js';
import { syncNsfResultsButton } from './nsf-results-ui.js';

// DOM Elements
const standingsList = document.getElementById('standings-list');
const btnReportRatings = document.getElementById('btn-report-ratings');

// The rule set the rating-change column is computed under: the group's bound club
// list when there is one, otherwise FIDE's (null lets the engine default).
//
// The config comes from a Firestore document but this render is synchronous, so
// the first paint after opening a bound tournament falls back to FIDE and then
// re-renders once the real config arrives. `awaitingListConfig` makes that happen
// exactly once — re-rendering from inside a render would otherwise loop forever.
let awaitingListConfig = false;

function ratingConfigFor(settings) {
    if (!settings.ratingDbId || !settings.ratingListId) return null;

    const cached = getCachedListConfig(settings.ratingDbId, settings.ratingListId);
    if (cached) return cached;

    if (!awaitingListConfig) {
        awaitingListConfig = true;
        ensureListConfig(settings.ratingDbId, settings.ratingListId)
            .then(config => { if (config) renderStandings(); })
            .finally(() => { awaitingListConfig = false; });
    }
    return null;
}

// "Report to Rating List" is only meaningful for a group bound to one of the
// arbiter's own rating lists, and only for the arbiter — a spectator watching
// someone else's tournament must never see it. A children's/youth tournament is
// not rated at all, so the button would offer to apply a calculation that this
// tournament has switched off.
function syncReportButton(settings) {
    if (!btnReportRatings) return;
    const usable = !!settings.ratingListId && !state.isSpectatorMode && !isYouthTournament(settings);
    btnReportRatings.style.display = usable ? '' : 'none';
    if (usable) {
        btnReportRatings.title = tr('Report to {list}', { list: settings.ratingListName || settings.ratingListId });
    }
}

// Display name for a tie-break descriptor (short header label).
function tbHeaderLabel(descriptor) {
    const p = window.TieBreak.parseDescriptor(descriptor);
    const n = p.name;
    const cut = p.modifiers.cutlow;
    const med = p.modifiers.cuthigh > 0;
    const map = {
        DE: 'DE', WIN: 'Wins', WON: 'Won', BPG: 'BPG', BWG: 'BWG', REP: 'REP',
        PS: 'Prog', KS: 'Koya', BH: 'BH', FB: 'FB', AOB: 'AOB', SB: 'SB',
        ARO: 'ARO', TPR: 'TPR', PTP: 'PTP', APRO: 'APRO', APPO: 'APPO'
    };
    let label = tr(map[n] || n);
    if (med && cut) label += `/M${cut}`;
    else if (cut) label += `-${cut}`;
    return label;
}

function tbFullName(descriptor) {
    const p = window.TieBreak.parseDescriptor(descriptor);
    const reg = window.TieBreak.REGISTRY.find(r => r.descriptor === p.descriptor);
    if (reg) return tr(reg.name);
    return descriptor;
}

// ---------------------------------------------------------------------------
// Team standings
//
// A separate table from the individual one, because it answers a different question.
// The individual standings are the board scores — what gets rated, and what a player
// looks up. These are what actually decides the competition: match points, board
// points, and the team tie-breaks from FIDE C.04.6.
//
// Rendered above the individual table rather than instead of it; see the container in
// index.html for why.
// ---------------------------------------------------------------------------

const TEAM_TB_LABELS = {
    mp: ['MP', 'Match points'],
    gp: ['BP', 'Board (game) points'],
    de: ['DE', 'Direct encounter — only applied when every tied team has met every other'],
    ttsb: ['SB', 'Team Sonneborn-Berger'],
    tbh: ['BH', 'Team Buchholz — the sum of the opponents\' scores'],
    mpo: ['MPO', 'Match points of the opponents faced']
};

// Half points read better as ½ than as .5 in a standings table.
function teamPts(n) {
    const whole = Math.floor(n);
    if (n - whole === 0.5) return (whole === 0 ? '' : whole) + '½';
    return String(n);
}

/**
 * An Arena's team table: each team's players' points added up
 * (window.ArenaEngine.teamStandings). It sits where a team tournament's table does, above
 * the individual standings, which still decide the individual places.
 */
function renderArenaTeamStandings(container, settings) {
    const teams = getTeams();
    if (teams.length === 0) { container.innerHTML = ''; return; }

    const table = window.ArenaEngine.teamStandings(teams, state.players, state.rounds, settings.pointSystem);
    const rows = table.map(row => {
        const team = teams.find(t => t.id === row.teamId) || {};
        return `
            <tr>
                <td style="text-align: center; font-weight: 700;">${row.rank}</td>
                <td><strong>${escapeHtml(row.name)}</strong>${team.club && team.club !== team.name ? `<div style="color: var(--text-muted); font-size: 0.75rem;">${escapeHtml(team.club)}</div>` : ''}</td>
                <td style="text-align: center; color: var(--text-muted);">${row.players}</td>
                <td style="font-weight: 700;">${teamPts(row.points)}</td>
                <td style="color: var(--text-muted);">${row.average.toFixed(2).replace('.', ',')}</td>
            </tr>`;
    }).join('');

    container.innerHTML = `
        <div class="card glass" style="margin-bottom: 1.5rem;">
            <div class="card-header" style="padding: 1rem 1.5rem;">
                <h3 style="margin: 0;"><i class="ri-team-line"></i> ${tr('Team Standings')}</h3>
                <span style="color: var(--text-muted); font-size: 0.8rem;">${tr('the points of every player in the team added up')}</span>
            </div>
            <div class="card-body">
                <div class="table-responsive">
                    <table class="data-table compact-table">
                        <thead>
                            <tr>
                                <th style="width: 50px;">${tr('Rank')}</th>
                                <th>${tr('Team')}</th>
                                <th style="width: 80px; text-align: center;">${tr('Players')}</th>
                                <th style="width: 90px;">${tr('Points')}</th>
                                <th style="width: 110px;" title="${escapeHtml(tr('Points per player, for comparing teams of different sizes'))}">${tr('Average')}</th>
                            </tr>
                        </thead>
                        <tbody>${rows}</tbody>
                    </table>
                </div>
            </div>
        </div>`;
}

export function renderTeamStandings() {
    const container = document.getElementById('team-standings-container');
    if (!container) return;

    const settings = getActiveSettings();
    if (settings.format === 'arena') { renderArenaTeamStandings(container, settings); return; }
    if (settings.format !== 'team') { container.innerHTML = ''; return; }

    const teams = getTeams();
    if (teams.length === 0) {
        container.innerHTML = `
            <div class="card glass" style="margin-bottom: 1.5rem;">
                <div class="card-body" style="text-align: center; padding: 2rem; color: var(--text-muted);">
                    ${tr('No teams have been registered yet.')}
                </div>
            </div>`;
        return;
    }

    const ts = settings.teamSettings;
    const table = window.TeamEngine.calculateTeamStandings(teams, state.rounds, {
        boards: ts.boards,
        pointSystem: settings.pointSystem,
        matchPointSystem: ts.matchPointSystem,
        primaryScore: ts.primaryScore,
        tiebreaks: ts.tiebreaks
    });

    // The primary score leads the table, so it is never also shown as a tie-break
    // column — it would be the same number twice under two headings.
    const primaryKey = ts.primaryScore === 'game' ? 'gp' : 'mp';
    const tbKeys = (ts.tiebreaks || []).filter(k => k !== primaryKey);

    const primaryLabel = ts.primaryScore === 'game' ? tr('Board Pts') : tr('Match Pts');
    const secondaryKey = primaryKey === 'mp' ? 'gp' : 'mp';
    const secondaryLabel = secondaryKey === 'gp' ? tr('Board Pts') : tr('Match Pts');

    const headers = tbKeys
        .filter(k => k !== secondaryKey)
        .map(k => {
            const [short, full] = TEAM_TB_LABELS[k] || [k.toUpperCase(), k];
            return `<th style="width: 80px;" title="${escapeHtml(tr(full))}">${escapeHtml(tr(short))}</th>`;
        }).join('');

    const rows = table.map(row => {
        const team = teams.find(t => t.id === row.teamId) || {};
        const cells = tbKeys.filter(k => k !== secondaryKey).map(k => {
            // Direct encounter is resolved inside a tie group against that group only, so
            // there is no single number to print for it — the ORDER is its output.
            if (k === 'de') return `<td style="color: var(--text-muted);">—</td>`;
            return `<td>${teamPts(row.tb[k] || 0)}</td>`;
        }).join('');

        const played = row.matchesPlayed;
        return `
            <tr>
                <td style="text-align: center; font-weight: 700;">${row.rank}</td>
                <td style="color: var(--text-muted); font-size: 0.85rem;">${row.teamNo}</td>
                <td><strong>${escapeHtml(team.name || '')}</strong>${team.club && team.club !== team.name ? `<div style="color: var(--text-muted); font-size: 0.75rem;">${escapeHtml(team.club)}</div>` : ''}</td>
                <td style="text-align: center; color: var(--text-muted);">${played}</td>
                <td style="font-weight: 700;">${teamPts(row.primary)}</td>
                <td>${teamPts(secondaryKey === 'gp' ? row.gamePoints : row.matchPoints)}</td>
                ${cells}
            </tr>`;
    }).join('');

    container.innerHTML = `
        <div class="card glass" style="margin-bottom: 1.5rem;">
            <div class="card-header" style="padding: 1rem 1.5rem;">
                <h3 style="margin: 0;"><i class="ri-team-line"></i> ${tr('Team Standings')}</h3>
                <span style="color: var(--text-muted); font-size: 0.8rem;">
                    ${tr('{boards} boards', { boards: ts.boards })} •
                    ${ts.primaryScore === 'game' ? tr('ranked by board points') : tr('ranked by match points')}
                </span>
            </div>
            <div class="card-body">
                <div class="table-responsive">
                    <table class="data-table compact-table">
                        <thead>
                            <tr>
                                <th style="width: 50px;">${tr('Rank')}</th>
                                <!-- Not tr('No'): the Norwegian dictionary renders that as
                                     "Nei" (the answer, not the abbreviation). A bare # needs
                                     no translation and cannot be mistaken. -->
                                <th style="width: 40px;" title="${tr('Team pairing number')}">#</th>
                                <th>${tr('Team')}</th>
                                <th style="width: 60px; text-align: center;" title="${tr('Matches played')}">${tr('Pld')}</th>
                                <th style="width: 90px;">${primaryLabel}</th>
                                <th style="width: 90px;">${secondaryLabel}</th>
                                ${headers}
                            </tr>
                        </thead>
                        <tbody>${rows}</tbody>
                    </table>
                </div>
            </div>
        </div>`;
}

export function renderStandings() {
    // Kept ahead of the early return below: the team table lives in its own container
    // and must still refresh on a screen where the individual table is absent.
    renderTeamStandings();

    // Which columns show depends on who is looking (js/ui/column-views.js).
    applyColumnVisibility();

    if (!standingsList) return;

    const settings = getActiveSettings();
    syncReportButton(settings);
    syncNsfResultsButton();
    const activeTiebreaks = settings.tiebreaks;
    const pointSystem = settings.pointSystem;
    const format = settings.format;
    const isRR = ['rr', 'drr', 'round-robin', 'roundrobin', 'double-rr', 'double-round-robin'].includes(format);
    const totalRounds = parseInt(settings.roundsCount) || state.rounds.length;
    // Konrad's best N of M. Only bites once more rounds have been played than count.
    const countedRounds = countedRoundsFor(settings);
    const countingSome = countedRounds > 0 && state.rounds.length > countedRounds;

    // Normalize legacy codes to descriptors for display + lookup.
    const descriptors = activeTiebreaks.map(c => window.TieBreak.legacyToDescriptor(c));

    // FIDE engine = source of truth for ranking and tie-break values.
    const tb = window.TieBreak.compute(state.players, state.rounds, {
        pointSystem, tiebreaks: activeTiebreaks, totalRounds, isRoundRobin: isRR, countedRounds
    });

    // AOR (Average Opponent Rating) and Perf (Tournament Performance Rating) get
    // fixed columns of their own whether or not they are active tie-breaks (the one
    // exception is a youth tournament — see showAor/showPerf below). They are
    // excluded from the dynamic tie-break columns to avoid duplication.
    const isPerfName = d => ['ARO', 'TPR'].includes(window.TieBreak.parseDescriptor(d).name);
    const displayDescriptors = descriptors.filter(d => !isPerfName(d));
    // Full screen mode hides AOR/Perf/Rating +/- unless ARO/TPR is itself one of
    // the tournament's chosen tie-breaks — see renderFullscreenStandings().
    const aroActive = descriptors.some(d => window.TieBreak.parseDescriptor(d).name === 'ARO');
    const tprActive = descriptors.some(d => window.TieBreak.parseDescriptor(d).name === 'TPR');

    /*
     * A children's and youth tournament drops both columns. They are ratings
     * arithmetic wearing a column heading — the average rating of the opponents,
     * and the rating this result would be worth — and in a tournament with no
     * rating calculation at all they read as a verdict on a nine-year-old that
     * nothing downstream will ever use.
     *
     * UNLESS the tournament actually ranks by one of them. ARO and TPR chosen as
     * TIE-BREAKS are shown in these same two columns rather than in a tie-break
     * column of their own (see displayDescriptors above), so hiding them there
     * would leave the standings ordered by a number the table never shows.
     */
    const youth = isYouthTournament(settings);
    /*
     * And a field where NOBODY is rated drops every rating column: the rating itself,
     * AOR, Perf and Rating +/- would be a column of blanks and zeros each. A school
     * tournament is usually exactly that, and on a phone those empty columns are what
     * pushed the tie-breaks off the right-hand edge.
     */
    const anyRated = state.players.some(p => (parseInt(p.rating, 10) || 0) > 0);
    const showRating = anyRated;
    const showAor = anyRated && (!youth || aroActive);
    const showPerf = anyRated && (!youth || tprActive);
    const showRatingChange = anyRated;
    const fixedCols = 4 + [showRating, showAor, showPerf, showRatingChange].filter(Boolean).length;
    const perfById = window.TieBreak.compute(state.players, state.rounds, {
        pointSystem, tiebreaks: ['ARO', 'TPR'], totalRounds, isRoundRobin: isRR
    }).byId;

    // SwissEngine still provides the rating change column — computed under the
    // group's bound rating list when there is one, so the number here is the one
    // reporting the tournament will actually apply.
    const legacyStandings = SwissEngine.calculateStandings(
        state.players, state.rounds, pointSystem, activeTiebreaks, ratingConfigFor(settings),
        { totalRounds, isRoundRobin: isRR, countedRounds }
    );
    const legacyById = {};
    legacyStandings.forEach(s => { legacyById[s.id] = s; });

    const headerRow = document.getElementById('standings-header-row');
    if (headerRow) {
        const tbHeaders = displayDescriptors.map(d =>
            `<th data-col="tb" style="width: 80px;" title="${tbFullName(d)}">${tbHeaderLabel(d)}</th>`
        ).join('');
        headerRow.innerHTML = `
            <th data-col="rank" style="width: 50px;">${tr('Rank')}</th>
            <th data-col="player" style="max-width: 320px;">${tr('Player')}</th>
            ${showRating ? `<th data-col="rating" style="width: 80px;">${tr('Rating')}</th>` : ''}
            <th data-col="points" style="width: 80px;"${countingSome ? ` title="${escapeHtml(tr('Best {n} of {m} rounds count; all rounds in brackets', { n: countedRounds, m: state.rounds.length }))}"` : ''}>${tr('Points')}</th>
            ${tbHeaders}
            ${showAor ? `<th data-col="aor" style="width: 80px;" title="${tr('Average Rating of Opponents')}">${tr('AOR')}</th>` : ''}
            ${showPerf ? `<th data-col="perf" style="width: 90px;" title="${tr('Tournament Performance Rating')}">${tr('Perf.')}</th>` : ''}
            ${showRatingChange ? `<th data-col="ratingchange" style="width: 90px;">${tr('Rating +/-')}</th>` : ''}
            <th data-col="opponents">${tr('Opponents')}</th>
        `;
    }

    renderFooterGuide(descriptors);

    if (tb.order.length === 0) {
        const colspan = fixedCols + displayDescriptors.length;
        standingsList.innerHTML = `<tr><td colspan="${colspan}" style="text-align: center; padding: 3rem; color: var(--text-muted);">${tr('No results recorded yet.')}</td></tr>`;
        renderFullscreenStandings({ aroActive, tprActive });
        return;
    }

    // Map player id -> displayed rank for opponent links.
    const rankMap = {};
    tb.order.forEach(r => { rankMap[r.id] = r.rank; });

    // Spectators get a club / nation / rating filter (hides non-matching rows but keeps ranks).
    if (state.isSpectatorMode) populateStandingsFilters();

    const rowsHtml = tb.order.map(r => {
        const legacy = legacyById[r.id] || {};
        const player = state.players.find(p => p.id === r.id) || legacy;

        // Filter (spectator only) — skip rows that don't match, ranks are unchanged.
        if (state.isSpectatorMode && !passesStandingsFilter(player, r.rating)) return '';

        // One entry PER GAME in round order (from the FIDE engine), not a deduplicated
        // set of opponent ids — so repeat encounters (double round robin, re-pairings)
        // and games against since-deleted players all stay visible.
        const opponentLinks = (r.opponents || [])
            .map(opp => {
                let rank = rankMap[opp.id];
                let isSpecial = opp.id === 'WO' || opp.id === 'BYE';
                if (!rank && isSpecial) rank = opp.id;
                if (!rank) rank = '?';
                let color = 'var(--text-main)';
                if (opp.result === 'win') color = 'var(--success)';
                else if (opp.result === 'draw') color = 'var(--warning)';
                else if (opp.result === 'loss') color = 'var(--danger)';
                if (isSpecial) {
                    return `<span style="color: ${color}; font-weight: 600;">${rank}</span>`;
                }
                return `<span class="clickable-player" data-id="${escapeHtml(opp.id)}" style="color: ${color}; font-weight: 600; cursor: pointer;" title="${tr('Player {rank}', { rank })}">${rank}</span>`;
            })
            .join(', ');

        const tbCells = displayDescriptors.map(d =>
            `<td data-col="tb">${window.TieBreak.formatValue(d, r.values[d], r)}</td>`
        ).join('');

        const perf = perfById[r.id] || { values: {} };
        const aorCell = window.TieBreak.formatValue('ARO', perf.values['ARO']);
        const perfCell = window.TieBreak.formatValue('TPR', perf.values['TPR']);

        const ratingChange = legacy.ratingChange;
        const ratingCell = (() => {
            if (!ratingChange || ratingChange === 0) return '-';
            const val = ratingChange.toFixed(1).replace('.', ',');
            const sign = ratingChange > 0 ? '+' : '';
            const color = ratingChange > 0 ? 'var(--success)' : (ratingChange < 0 ? 'var(--danger)' : 'var(--text-main)');
            return `<span style="color: ${color}; font-weight: 600;">${sign}${val}</span>`;
        })();

        const ratingVal = (r.rating && r.rating !== 0) ? r.rating : '';

        return `
            <tr data-player-id="${escapeHtml(r.id)}">
                <td data-col="rank"><strong>${r.rank}</strong></td>
                <td data-col="player" style="max-width: 320px;"><div class="tbl-title clickable-player" data-id="${escapeHtml(r.id)}" style="cursor: pointer; color: var(--primary); white-space: normal; word-break: break-word;" title="${escapeHtml(formatPlayerName(player, false))}">${formatPlayerName(player, true)}</div></td>
                ${showRating ? `<td data-col="rating"><span style="font-weight: 500;">${escapeHtml(ratingVal)}</span></td>` : ''}
                <td data-col="points"><span class="points-value">${r.points}</span>${countingSome && r.totalPoints !== r.points ? ` <span style="color: var(--text-muted); font-size: 0.8rem;" title="${escapeHtml(tr('All rounds'))}">(${r.totalPoints})</span>` : ''}</td>
                ${tbCells}
                ${showAor ? `<td data-col="aor">${aorCell}</td>` : ''}
                ${showPerf ? `<td data-col="perf">${perfCell}</td>` : ''}
                ${showRatingChange ? `<td data-col="ratingchange">${ratingCell}</td>` : ''}
                <td data-col="opponents"><div class="tbl-subtitle" style="max-width: 350px; white-space: normal; word-break: break-word;" title="${opponentLinks.replace(/<[^>]*>/g, '')}">${opponentLinks}</div></td>
            </tr>
        `;
    }).filter(Boolean);

    if (rowsHtml.length === 0) {
        const colspan = fixedCols + displayDescriptors.length;
        standingsList.innerHTML = `<tr><td colspan="${colspan}" style="text-align: center; padding: 3rem; color: var(--text-muted);">${tr('No players match the current filters.')}</td></tr>`;
    } else {
        standingsList.innerHTML = rowsHtml.join('');
    }

    // Re-apply any active player-search highlight after a (re)render — no scroll,
    // so live result updates don't yank the page around.
    applyStandingsSearchHighlight(false);

    renderFullscreenStandings({ aroActive, tprActive });

    // The crosstable and the prize list are the same ranking in other shapes, behind tabs
    // on this page, so a result entered while one of them is open has to reach it too.
    refreshActiveStandingsView();
}

// ---------------------------------------------------------------------------
// Full Screen Standings View — mirrors the current standings table into a
// distraction-free overlay (same pattern as the Pairings full screen view),
// trimmed to the columns that matter at a glance (rank, player, rating,
// points, active tie-breaks, opponents) and split across up to two balanced
// columns when there are enough players to warrant it.
// ---------------------------------------------------------------------------

let lastStandingsFlags = { aroActive: false, tprActive: false };

// The rows the full screen view shows, cloned from the tab's table with the
// columns full screen does not show already stripped. Kept apart from the DOM
// because the same rows are dealt out again every time the page is turned, the
// screen changes size, or a result comes in.
let fsHeader = null;
let fsRows = [];
let fsPage = 0;

function renderFullscreenStandings(flags) {
    if (flags) lastStandingsFlags = flags;
    const { aroActive, tprActive } = lastStandingsFlags;

    const headerRow = document.getElementById('standings-header-row');
    if (!headerRow || !standingsList) {
        fsHeader = null;
        fsRows = [];
        layoutFullscreenStandings();
        return;
    }

    // Always-hidden columns aren't part of the "at a glance" view; AOR/Perf are
    // shown only when the tournament actually uses ARO/TPR as a tie-break. That
    // covers the youth case for free: a youth tournament renders those columns
    // only when they ARE the tie-break, and there is nothing to strip otherwise.
    const hideCols = new Set(['ratingchange']);
    if (!aroActive) hideCols.add('aor');
    if (!tprActive) hideCols.add('perf');

    const stripHiddenCols = (row) => {
        Array.from(row.children).forEach(cell => {
            if (hideCols.has(cell.getAttribute('data-col'))) cell.remove();
        });
        return row;
    };

    fsHeader = stripHiddenCols(headerRow.cloneNode(true));
    fsHeader.removeAttribute('id');
    fsRows = Array.from(standingsList.querySelectorAll('tr'))
        .map(row => stripHiddenCols(row.cloneNode(true)));

    layoutFullscreenStandings();
}

// Put rows [start, end) of each chunk into a column of its own. An empty chunk
// next to a full one is an empty column, so the last page of a two-column table
// keeps its columns where every other page had them.
function buildFullscreenColumns(chunks) {
    const body = document.getElementById('standings-fullscreen-body');
    body.innerHTML = '';
    chunks.forEach(([start, end]) => {
        const col = document.createElement('div');
        col.className = 'fs-standings-column';

        if (start < end || chunks.length === 1) {
            const thead = document.createElement('thead');
            thead.appendChild(fsHeader.cloneNode(true));
            const tbody = document.createElement('tbody');
            for (let i = start; i < end; i++) tbody.appendChild(fsRows[i].cloneNode(true));

            const table = document.createElement('table');
            table.appendChild(thead);
            table.appendChild(tbody);
            col.appendChild(table);
        }
        body.appendChild(col);
    });
}

// Full screen never scrolls. A TV has nobody standing at it to scroll, and a
// column running off the bottom of the screen hides exactly the players still
// looking for their names — so the rows are dealt into pages of what the screen
// really holds, and the arbiter turns the page.
//
// How much it holds is measured from a real heading and a real row rather than
// worked out from the CSS, and then checked: if a column still overflows (a
// horizontal scrollbar on a phone, a row taller than the first), it is laid out
// again with one row fewer per column.
//
// One column up to 20 players, as before, as long as they fit in it; otherwise
// two, split top-heavy (21 -> 11 + 10). Never two below 900px wide, where half
// the screen is not a column, it is a place where nothing fits.
function layoutFullscreenStandings() {
    const overlay = document.getElementById('standings-fullscreen-overlay');
    const body = document.getElementById('standings-fullscreen-body');
    if (!overlay || !body) return;

    // Nothing can be measured while the overlay is display:none; opening it
    // lays it out again.
    if (!overlay.classList.contains('active') || !fsHeader) {
        body.innerHTML = '';
        syncFullscreenPager(1);
        return;
    }

    const count = fsRows.length;
    if (count === 0) {
        fsPage = 0;
        buildFullscreenColumns([[0, 0]]);
        syncFullscreenPager(1);
        return;
    }

    buildFullscreenColumns([[0, 1]]);
    const probe = body.querySelector('table');
    const headHeight = probe.tHead.getBoundingClientRect().height;
    const rowHeight = probe.tBodies[0].rows[0].getBoundingClientRect().height;
    let perCol = rowHeight > 0
        ? Math.max(1, Math.floor((body.clientHeight - headHeight) / rowHeight))
        : count;

    const wide = window.innerWidth >= 900;
    for (;;) {
        const cols = (!wide || (count <= 20 && count <= perCol)) ? 1 : 2;
        const perPage = cols * perCol;
        const pages = Math.ceil(count / perPage);
        fsPage = Math.min(Math.max(fsPage, 0), pages - 1);

        const start = fsPage * perPage;
        const end = Math.min(start + perPage, count);
        const split = cols === 1 ? end : start + Math.ceil((end - start) / 2);
        buildFullscreenColumns(cols === 1 ? [[start, end]] : [[start, split], [split, end]]);
        fitFullscreenText();
        syncFullscreenPager(pages);

        const overflows = Array.from(body.querySelectorAll('.fs-standings-column'))
            .some(col => col.scrollHeight > col.clientHeight + 1);
        if (!overflows || perCol === 1) return;
        perCol--;
    }
}

function syncFullscreenPager(pages) {
    const pager = document.getElementById('standings-fullscreen-pager');
    if (!pager) return;
    pager.hidden = pages <= 1;

    const label = document.getElementById('standings-fullscreen-page');
    if (label) label.textContent = tr('Page {page} of {pages}', { page: fsPage + 1, pages });
    const prev = document.getElementById('btn-fs-standings-prev');
    const next = document.getElementById('btn-fs-standings-next');
    if (prev) prev.disabled = fsPage <= 0;
    if (next) next.disabled = fsPage >= pages - 1;
}

// Turn the page by `step` (+1 next, -1 previous). Clamped by the layout, so a
// press on the last page does nothing rather than wrapping round to the leader.
export function turnFullscreenStandingsPage(step) {
    const overlay = document.getElementById('standings-fullscreen-overlay');
    if (!overlay || !overlay.classList.contains('active')) return;
    fsPage += step;
    layoutFullscreenStandings();
}

// The two text columns — the player's name and the list of opponents — are the
// only ones that can be any width, so they are the ones given whatever room the
// numbers leave over, and a name too long for its share is shrunk until it fits
// on ONE line rather than wrapped. A wrapped name would make its row taller
// than the rest and break the ladder of ranks the room is scanning.
//
// It is measured, not guessed from the character count: how much room a name
// gets depends on how many tie-break columns the tournament shows and on
// whether the table is split into one column or two.
//
// The opponents are served first but capped, because what they need is bounded
// — a handful of rank numbers — while a name can be any length; everything they
// leave goes to the name. FS_MIN_SCALE is how far each may then be shrunk. The
// opponents' floor is the higher of the two: they are small numbers already,
// and shrinking those to the point of illegibility helps nobody, so past it
// they simply clip.
const FS_OPPONENT_SHARE = 0.32;
const FS_MIN_SCALE = { player: 0.55, opponents: 0.8 };

// Under this there is nothing worth dividing: a phone, or a tournament running
// so many tie-break columns that the numbers alone fill the screen. Squeezing
// the names into what is left would leave them unreadable, so they keep their
// size and the table scrolls sideways instead.
const FS_MIN_SPARE = 180;

// Nothing can be measured while the overlay is display:none, so this runs when
// it opens and on every later change of size. Each run starts over from the
// stylesheet's own size, so a screen that grows again gives the names their
// full size back.
function fitFullscreenText() {
    const overlay = document.getElementById('standings-fullscreen-overlay');
    if (!overlay || !overlay.classList.contains('active')) return;

    overlay.querySelectorAll('.fs-standings-column').forEach(column => {
        const table = column.querySelector('table');
        const avail = column.clientWidth;
        if (!table || !avail) return;

        const cells = {};
        const heads = [];
        ['player', 'opponents'].forEach(col => {
            cells[col] = Array.from(column.querySelectorAll(`td[data-col="${col}"] > div`));
            cells[col].forEach(el => {
                el.style.fontSize = '';
                el.style.maxWidth = '0px';
            });
            column.querySelectorAll(`th[data-col="${col}"]`).forEach(th => heads.push(th));
        });

        // With both elastic columns squeezed to nothing — their own headings
        // included — the table's natural width is exactly what everything else
        // needs, so the rest is theirs to share and the table ends up filling
        // its column instead of overflowing it.
        heads.forEach(th => { th.style.fontSize = '0'; });
        table.style.width = 'max-content';
        const needed = table.getBoundingClientRect().width;
        table.style.width = '';
        heads.forEach(th => { th.style.fontSize = ''; });
        const spare = Math.max(0, avail - needed);
        if (spare < FS_MIN_SPARE) {
            Object.values(cells).forEach(list => list.forEach(el => { el.style.maxWidth = 'none'; }));
            return;
        }

        // A box collapsed to nothing still reports the width of the text inside
        // it, so this is what the longest opponent list would want at full size.
        const opponentsWant = cells.opponents.reduce((w, el) => Math.max(w, el.scrollWidth), 0);
        const widths = {
            opponents: Math.min(opponentsWant, Math.floor(spare * FS_OPPONENT_SHARE))
        };
        widths.player = spare - widths.opponents;

        Object.entries(widths).forEach(([col, width]) => {
            const min = FS_MIN_SCALE[col];
            cells[col].forEach(el => {
                el.style.maxWidth = `${width}px`;

                // clientWidth is the box the text has to live in; scrollWidth is
                // what one unwrapped line of it actually wants. Two passes: the
                // first ratio lands close, the second corrects for the rounding
                // and hinting that shifts once the text is smaller.
                const box = el.clientWidth;
                const base = parseFloat(getComputedStyle(el).fontSize) || 0;
                if (!box || !base) return;
                const floor = base * min;

                let size = base;
                for (let pass = 0; pass < 2 && el.scrollWidth > box; pass++) {
                    size = Math.max(floor, size * (box / el.scrollWidth) - 0.5);
                    el.style.fontSize = `${size}px`;
                    if (size <= floor) break;
                }
            });
        });
    });
}

// The overlay's size settles a frame or two after the browser hands the screen
// over, so laying it out once on open would deal the pages for the windowed
// size instead.
function scheduleFullscreenLayout() {
    layoutFullscreenStandings();
    requestAnimationFrame(layoutFullscreenStandings);
}

export function openFullscreenStandings() {
    const overlay = document.getElementById('standings-fullscreen-overlay');
    if (!overlay) return;

    // Active first: the layout measures the screen, and a hidden overlay has none.
    fsPage = 0;
    overlay.classList.add('active');
    renderFullscreenStandings();
    scheduleFullscreenLayout();

    if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch(() => {});
    }
}

export function closeFullscreenStandings() {
    const overlay = document.getElementById('standings-fullscreen-overlay');
    if (!overlay || !overlay.classList.contains('active')) return;

    overlay.classList.remove('active');

    if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
    }
}

document.addEventListener('fullscreenchange', () => {
    if (document.fullscreenElement) { scheduleFullscreenLayout(); return; }
    const overlay = document.getElementById('standings-fullscreen-overlay');
    if (overlay) overlay.classList.remove('active');
});

// Re-measure on resize: a projector that switches resolution, a rotated tablet,
// or simply leaving full screen again all change how many rows a page holds and
// what a name has room for.
let fsLayoutTimer = null;
window.addEventListener('resize', () => {
    clearTimeout(fsLayoutTimer);
    fsLayoutTimer = setTimeout(layoutFullscreenStandings, 150);
});

// Arrow keys and Page Up/Down turn the page. Page Up/Down is what a presenter
// clicker sends, so the arbiter can turn it from across the hall. Taken in the
// capture phase and stopped there: the tab's own hotkeys are user-assigned and
// may well include an arrow, and they would otherwise fire on the tab hidden
// behind the overlay.
const FS_PAGE_KEYS = { ArrowRight: 1, PageDown: 1, ArrowLeft: -1, PageUp: -1 };
window.addEventListener('keydown', (e) => {
    const step = FS_PAGE_KEYS[e.key];
    if (!step || e.altKey || e.ctrlKey || e.metaKey) return;
    const overlay = document.getElementById('standings-fullscreen-overlay');
    if (!overlay || !overlay.classList.contains('active')) return;
    e.preventDefault();
    e.stopPropagation();
    if (!e.repeat) turnFullscreenStandingsPage(step);
}, true);

window.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || document.fullscreenElement) return;
    const overlay = document.getElementById('standings-fullscreen-overlay');
    if (overlay && overlay.classList.contains('active')) closeFullscreenStandings();
});

// ----- spectator standings filters (club / nation / rating range) -----

function standingsDistinct(arr) {
    return [...new Set(arr.map(v => (v || '').trim()).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));
}

function standingsFillSelect(sel, values, allLabel) {
    if (!sel) return;
    const prev = sel.value;
    sel.innerHTML = [`<option value="">${allLabel}</option>`]
        .concat(values.map(v => `<option value="${escapeHtml(v)}">${escapeHtml(v)}</option>`))
        .join('');
    sel.value = values.includes(prev) ? prev : '';
}

function populateStandingsFilters() {
    standingsFillSelect(document.getElementById('filter-standings-club'),
        standingsDistinct(state.players.map(p => p.club)), tr('All clubs'));
    standingsFillSelect(document.getElementById('filter-standings-fed'),
        standingsDistinct(state.players.map(p => p.fed)), tr('All nations'));
}

function passesStandingsFilter(player, rating) {
    const clubEl = document.getElementById('filter-standings-club');
    const fedEl = document.getElementById('filter-standings-fed');
    const minEl = document.getElementById('filter-standings-rating-min');
    const maxEl = document.getElementById('filter-standings-rating-max');
    const club = clubEl ? clubEl.value : '';
    const fed = fedEl ? fedEl.value : '';
    const minR = minEl && minEl.value !== '' ? parseInt(minEl.value) : null;
    const maxR = maxEl && maxEl.value !== '' ? parseInt(maxEl.value) : null;

    if (club && (player.club || '').trim() !== club) return false;
    if (fed && (player.fed || '').trim() !== fed) return false;
    if (minR !== null || maxR !== null) {
        const r = parseInt(rating) || 0;
        if (minR !== null && r < minR) return false;
        if (maxR !== null && r > maxR) return false;
    }
    return true;
}

// Highlight the standings row(s) matching the search box (does NOT filter rows).
// When `scroll` is true (user typed), smooth-scroll the FIRST match into view so
// the user always jumps to the first or only player that matches what they typed.
export function applyStandingsSearchHighlight(scroll = false) {
    if (!standingsList) return;
    const input = document.getElementById('standings-search');
    const q = input ? input.value.trim().toLowerCase() : '';
    const rows = standingsList.querySelectorAll('tr[data-player-id]');
    rows.forEach(r => r.classList.remove('row-highlight'));
    if (!q) return;

    const matches = [];
    rows.forEach(r => {
        const id = r.getAttribute('data-player-id');
        const player = state.players.find(p => String(p.id) === String(id));
        if (!player) return;
        const given = (player.givenName || '').toLowerCase();
        const last = (player.lastName || '').toLowerCase();
        if (`${given} ${last}`.includes(q) || `${last} ${given}`.includes(q)) {
            r.classList.add('row-highlight');
            matches.push(r);
        }
    });
    if (scroll && matches.length >= 1) {
        queueScrollToRow(matches[0].getAttribute('data-player-id'));
    }
}

// Nearest ancestor that actually scrolls vertically. On desktop that is the
// standings card's own body (css/main.css); at <=768px the document itself
// scrolls instead.
function getScrollParent(el) {
    for (let p = el.parentElement; p; p = p.parentElement) {
        const oy = getComputedStyle(p).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) return p;
    }
    return document.scrollingElement || document.documentElement;
}

let pendingScrollTimer = null;
let scrollAnimFrame = null;
let scrollSettleTimer = null;

// Wait for a pause in typing before scrolling. Every keystroke re-targets the
// first match, and re-issuing a scroll on each one restarts it from a standstill,
// so a fast typist never sees it move.
function queueScrollToRow(playerId) {
    clearTimeout(pendingScrollTimer);
    pendingScrollTimer = setTimeout(() => scrollRowToCentre(playerId), 140);
}

// Centre the row for `playerId` in its scrollport. Resolved by id rather than by
// element reference because renderStandings() replaces the whole <tbody>, so a row
// captured before the debounce may be detached by the time we run.
function scrollRowToCentre(playerId) {
    if (!standingsList) return;
    const row = standingsList.querySelector(`tr[data-player-id="${CSS.escape(playerId)}"]`);
    if (!row) return;

    const scroller = getScrollParent(row);
    const isDoc = scroller === document.scrollingElement || scroller === document.documentElement;
    const rowRect = row.getBoundingClientRect();
    const viewTop = isDoc ? 0 : scroller.getBoundingClientRect().top;
    const viewHeight = isDoc ? window.innerHeight : scroller.clientHeight;
    // Offset of the row from the top of the scrollport, minus half a viewport,
    // puts the row in the middle.
    const delta = (rowRect.top - viewTop) - (viewHeight / 2 - rowRect.height / 2);
    const max = scroller.scrollHeight - scroller.clientHeight;
    const target = Math.max(0, Math.min(scroller.scrollTop + delta, max));
    animateScrollTop(scroller, target);
}

// Animate scrollTop by hand instead of using scrollTo({behavior:'smooth'}).
// Native smooth scrolling silently fails to animate in some Chrome setups — the
// call is accepted and scrollTop never moves — whereas assigning scrollTop always
// takes effect. This keeps the glide without depending on that.
//
// The glide is presentation only: requestAnimationFrame is starved whenever the
// page is not compositing (background tab, remote/headless viewer), so a timer
// guarantees we still end up at `target` even if not a single frame runs.
function animateScrollTop(scroller, target) {
    cancelAnimationFrame(scrollAnimFrame);
    clearTimeout(scrollSettleTimer);
    const start = scroller.scrollTop;
    const distance = target - start;
    if (Math.abs(distance) < 1) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        scroller.scrollTop = target;
        return;
    }
    const duration = 320;
    let startTime = null;
    const step = now => {
        if (startTime === null) startTime = now;
        const p = Math.min((now - startTime) / duration, 1);
        const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        scroller.scrollTop = start + distance * eased;
        if (p < 1) scrollAnimFrame = requestAnimationFrame(step);
    };
    scrollAnimFrame = requestAnimationFrame(step);
    scrollSettleTimer = setTimeout(() => {
        cancelAnimationFrame(scrollAnimFrame);
        if (Math.abs(scroller.scrollTop - target) > 1) scroller.scrollTop = target;
    }, duration + 80);
}

function renderFooterGuide(descriptors) {
    const footerContainer = document.getElementById('active-tiebreaks-guide-list');
    if (!footerContainer) return;
    if (!state.activeTournament) {
        footerContainer.innerHTML = `<span style="color: var(--text-muted);">${tr('No active tournament.')}</span>`;
        return;
    }
    if (descriptors.length === 0) {
        footerContainer.innerHTML = `<span style="color: var(--text-muted);">${tr('None active (ranking purely by points).')}</span>`;
        return;
    }
    // The column heading's abbreviation beside the full name: the headings carry the
    // full name as a tooltip, but a phone has no hover, and "BH-1" or "Innb." on its
    // own explains nothing to a parent reading the standings.
    footerContainer.innerHTML = descriptors.map((d, index) => {
        const short = tbHeaderLabel(d);
        const full = tbFullName(d);
        const abbr = short && short !== full ? `<abbr title="${escapeHtml(full)}" style="text-decoration: none; color: var(--text-main); font-weight: 700;">${short}</abbr> = ` : '';
        return `<div style="display: flex; align-items: center; gap: 0.25rem; font-weight: 600;">
            <span style="color: var(--primary); font-weight: 700;">${index + 1}.</span>
            <span style="color: var(--text-main);">${abbr}${full}</span>
        </div>`;
    }).join('');
}
